import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createHashRouter, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AgentPromoRoute,
  ReferrerPromoRoute,
  RequireAuth,
  createSupabaseClient,
  ToastProvider,
  GlobalErrorToast,
  AuthCacheReset,
  setGapiBase,
} from '@dogdisk/shared'
import './index.css'
import { RootLayout } from './components/root-layout'

// gapi 组前缀:user SPA 挂在 /gapi/user router group(gapi-restful-regroup)。
setGapiBase('/gapi/user')

import { startAutoNodeScheduler } from './_lib/resource-node-auto'

import './i18n'
import { ForumPage } from './routes/forum'
import { DatingComingSoonPage } from './routes/dating'
import { LoginPage } from './routes/auth/login'
import LoginSavedPage from './routes/auth/login-saved'
import { RegisterPage } from './routes/auth/register'
import { ForgotPasswordPage } from './routes/auth/forgot-password'
import { TwoFactorPage } from './routes/auth/two-factor'
import { FilesPage } from './routes/files'
import { SharePackPage } from './routes/share-pack'
import { PackFilesPage } from './routes/pack-files'
import { WalletPage } from './routes/wallet'
import { RechargePage } from './routes/wallet/recharge'
import { RechargeCallbackPage } from './routes/recharge-callback'
import { TransferPage } from './routes/wallet/transfer'
import { TransferRecordsPage } from './routes/wallet/transfer-records'
import { WithdrawPage } from './routes/wallet/withdraw'
import { WithdrawRecordsPage } from './routes/wallet/withdraw-records'
import { VipPage } from './routes/vip'
import { MessagesPage } from './routes/messages'
import { MessageThreadPage } from './routes/message-thread'
import { MessageNewPage } from './routes/messages/new'
import { TicketNewPage } from './routes/ticket-new'
import { TicketDetailPage } from './routes/ticket-detail'
import { ProfilePage } from './routes/profile'
import { MfaPage } from './routes/profile/mfa'
import BackupCodesPage from './routes/profile/backup-codes'
import { ProfileEditPage } from './routes/profile/edit'
import { NotificationsPage } from './routes/profile/notifications'
import { PayoutAccountsPage } from './routes/profile/payout-accounts'
import { OAuthBindingsPage } from './routes/profile/oauth-bindings'
import { ChangePasswordPage } from './routes/profile/change-password'
import { DeactivateAccountPage } from './routes/profile/deactivate-account'
import SettingsPage from './routes/profile/settings'
import LanguagePage from './routes/profile/language'
import EmailBindPage from './routes/profile/email-bind'
import ChangeEmailPage from './routes/profile/change-email'
import RegisterEmailVerifyPage from './routes/auth/email-verify'
import AboutPage from './routes/about'
import OAuthCallbackPage from './routes/oauth-callback'
import { InvitePage } from './routes/invite'
import { PackCreatePage } from './routes/pack-create'
import { OrdersPage } from './routes/orders'
import { SearchPage } from './routes/search'
import { FilePreviewPage } from './routes/file-preview'
import { PreviewPage } from './routes/file-preview-public'
import { ArticleDetailPage } from './routes/article-detail'
import { ArticleEditPage } from './routes/article-edit'
import { MyArticlesPage } from './routes/my-articles'
import { UserProfilePage } from './routes/user-profile'
import { VipBuyPage } from './routes/vip/buy'
import { VipOrderDetailPage } from './routes/vip/orders/[id]'
import { PackManagePage } from './routes/pack-manage'
import { OwnerPackFilesPage } from './routes/pack-owner-files'
import { TrashPage } from './routes/trash'
import { PackTrashPage } from './routes/pack-trash'
import { FolderDetailPage } from './routes/folder-detail'
import { TicketsPage } from './routes/tickets'
import { TransfersPage } from './routes/transfers'
import { ForbiddenPage } from './routes/errors/forbidden'
import { NotFoundPage } from './routes/errors/not-found'
import { ServerErrorPage } from './routes/errors/server-error'
import { MaintenancePage } from './routes/errors/maintenance'
import { OfflinePage } from './routes/errors/offline'
import { AgentErrorPage } from './routes/errors/agent-error'
import { InstallPrompt } from './components/install-prompt'
import { GlobalCaptchaGate } from './components/global-captcha-gate'

// VITE_SUPABASE_URL 空 → 默认同源（Caddy 反代 /auth/v1 /rest/v1 /storage/v1
// /realtime/v1 到 supabase Kong）。docker build --build-arg 注入 anon key；
// e2e 测试可继续用 fake key 不发真请求。
const supabaseUrl = import.meta.env['VITE_SUPABASE_URL'] || window.location.origin
const supabasePublishableKey = import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY']
if (import.meta.env.PROD && !supabasePublishableKey) {
  throw new Error('VITE_SUPABASE_PUBLISHABLE_KEY 缺失:生产构建必须 docker build --build-arg 注入')
}
createSupabaseClient({
  url: supabaseUrl,
  anonKey:
    supabasePublishableKey ??
    'sb_publishable_fake_dev_0000000000000000000000_aaaa1111',
})

// 首访/6h 资源节点自动测速(静默;须在 supabase client 创建后启动,
// spec 2026-07-13-resource-node-switch-client-design §3.3)
startAutoNodeScheduler()

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
})

// 路由守卫:🟢 Public / 🔒 Authed / 🔀 Mixed 分类对齐 docs/ui/user-page-flows.md §4.
// 🔀 Mixed 页面级判 session 内部切视图(不裹 RequireAuth):
//   /            永久重定向 -> /forum(2026-06-05 module spec:首页改名论坛)
//   /forum       ForumPage LNMX9 — 论坛文章流(UGC;无需登录;锁 module=forum)
//   /dating      DatingComingSoonPage K22D4 — 约爱占位页(尚未发布/敬请期待;本期)
//   /preview/:fileId 别人的文件预览页(按 mime 渲染 q5D4P/AGXin/Qk72B/AFxaz;匿名可看)
//                  (/file/:fileId 自己的文件详情移到 Authed 块)
//   /s/:packId   分享接收页;[保存到我的网盘] GuardedButton 兜底
//   /profile     EKOSx 未登录引导 / QQKOK 已登录卡片(实现合并到一文件,内部分流)
//   /my-shares   alias -> /files?view=shares(replace);分享列表合进文件页 segment
const authed = (el: React.ReactElement) => <RequireAuth>{el}</RequireAuth>
// title 文案集中表:key = 路由 path,value = 浏览器 title(纯页面名,无品牌后缀)。
// 未列出的 path(重定向 / 推广 / catch-all)→ 无 handle → title 回退为空。
const ROUTE_TITLES: Record<string, string> = {
  '/forum': '论坛',
  '/dating': '约爱',
  '/preview/:fileId': '文件预览',
  '/article/create': '写文章',
  '/article/:id/edit': '编辑文章',
  '/article/:id': '文章',
  '/me/articles': '我的文章',
  '/s/:packId': '资源包',
  '/s/:packId/files': '资源包文件',
  '/pack/:packId': '资源包',
  '/pack/:packId/files': '资源包文件',
  '/profile': '我的',
  '/file/:fileId': '文件详情',
  '/files': '我的文件',
  '/wallet': '钱包',
  '/wallet/recharge': '充值',
  '/recharge/callback': '充值结果',
  '/wallet/transfer': '转账',
  '/wallet/transfer/records': '转账记录',
  '/wallet/withdraw': '提现',
  '/wallet/withdraw/records': '提现记录',
  '/vip': 'VIP 会员',
  '/vip/buy': '购买 VIP',
  '/vip/orders/:id': 'VIP 订单',
  '/pack/manage/:id': '资源包管理',
  '/pack/manage/:id/files': '资源包文件',
  '/trash': '回收站',
  '/pack-trash': '资源包回收站',
  '/folders/:id': '文件夹',
  '/tickets': '工单',
  '/transfers': '传输列表',
  '/messages': '私信',
  '/messages/new': '新建私信',
  '/messages/:conversationId': '私信',
  '/tickets/new': '新建工单',
  '/tickets/:ticketId': '工单详情',
  '/profile/change-password': '修改密码',
  '/profile/settings': '设置',
  '/profile/language': '语言',
  '/profile/email-bind': '验证邮箱',
  '/profile/change-email': '更换邮箱',
  '/profile/mfa': '两步验证',
  '/profile/mfa/backup-codes': '备份码',
  '/profile/edit': '编辑资料',
  '/profile/notifications': '通知设置',
  '/profile/payout-accounts': '收款账户',
  '/profile/oauth-bindings': '第三方绑定',
  '/profile/deactivate': '注销账户',
  '/invite': '邀请好友',
  '/pack/create': '创建资源包',
  '/orders': '订单',
  '/search': '搜索',
  '/about': '关于',
  '/auth/login': '登录',
  '/auth/login/saved': '登录',
  '/auth/register': '注册',
  '/auth/forgot-password': '找回密码',
  '/auth/2fa': '两步验证',
  '/auth/email-verify': '邮箱验证',
  '/oauth/callback': '授权回调',
  '/u/:userId': '用户主页',
  '/403': '无权访问',
  '/404': '页面不存在',
  '/500': '服务器错误',
  '/503': '维护中',
  '/offline': '离线',
  '/agent-error': '代理异常',
}

const routes = [
  // 🔀 Mixed:页面内部判 session 切视图
  // 2026-06-05 module spec:首页改名论坛(/forum);约爱 /dating 本期占位页;旧 '/' 永久重定向
  { path: '/', element: <Navigate to="/forum" replace /> },
  { path: '/forum', element: <ForumPage /> },
  { path: '/dating', element: <DatingComingSoonPage /> },
  // 2026-05-31 按归属拆分(spec user-file-preview-detail-split):
  //   /preview/:fileId 别人的文件预览(🔀 Mixed;q5D4P/AGXin/Qk72B/AFxaz;社交/转存,未登录跳 login)
  //   /file/:fileId    自己的文件详情(🔒 Authed,见下方 authed 块;zkY66/kENKt/qCOtS/pKcdM)
  { path: '/preview/:fileId', element: <PreviewPage /> },
  // 文章(UGC)— 2026-06-02 D1。详情/编辑/我的 全 🔒 Authed
  //  (详情原 🔀 Mixed;2026-06-13 owner 决策:匿名禁访文章详情,feed 摘要 /forum 保留匿名)。
  //   /article/create + /article/:id/edit(静态段 create 比 param 更具体,data router 优先匹配)
  { path: '/article/create', element: authed(<ArticleEditPage />) },
  { path: '/article/:id/edit', element: authed(<ArticleEditPage />) },
  { path: '/article/:id', element: authed(<ArticleDetailPage />) },
  { path: '/me/articles', element: authed(<MyArticlesPage />) },

  // 外部分享落地(设归因 cookie;仅手动复制链接 / 二维码产生此 URL)
  { path: '/s/:packId', element: <SharePackPage promo /> },
  { path: '/s/:packId/files', element: <PackFilesPage /> },
  // 站内资源包详情(不设归因 cookie;文章 / 搜索 / 主页 / 订阅列表等站内入口;2026-06-13 pending §11)
  { path: '/pack/:packId', element: <SharePackPage /> },
  { path: '/pack/:packId/files', element: <PackFilesPage /> },
  { path: '/profile', element: <ProfilePage /> },
  { path: '/my-shares', element: <Navigate to="/files?view=shares" replace /> },

  // 🔒 Authed
  // 自己的文件详情(原 Mixed /file 合并页拆分后:owner 视角,需登录;DetailPage = FilePreviewPage)
  { path: '/file/:fileId', element: authed(<FilePreviewPage />) },
  { path: '/files', element: authed(<FilesPage />) },
  { path: '/wallet', element: authed(<WalletPage />) },
  { path: '/wallet/recharge', element: authed(<RechargePage />) },
  // B3-#9 MUGdo 充值回调页(用户从支付提供商回跳)
  { path: '/recharge/callback', element: authed(<RechargeCallbackPage />) },
  { path: '/wallet/transfer', element: authed(<TransferPage />) },
  { path: '/wallet/transfer/records', element: authed(<TransferRecordsPage />) },
  { path: '/wallet/withdraw', element: authed(<WithdrawPage />) },
  { path: '/wallet/withdraw/records', element: authed(<WithdrawRecordsPage />) },
  { path: '/vip', element: authed(<VipPage />) },
  // B-Pages #2 hK08h VIP 购买独立路由(替代旧 VipPage 内 view='buy' 二态)
  { path: '/vip/buy', element: authed(<VipBuyPage />) },
  // D5 a5cbV0 VIP 订单详情独立 route(orders.tsx 仅列表 + drawer;本 frame 独立 page)
  { path: '/vip/orders/:id', element: authed(<VipOrderDetailPage />) },
  // KkJt4 资源包管理(原 share-manage 详情+设置合一;2026-06-12 pack-share-rework)
  { path: '/pack/manage/:id', element: authed(<PackManagePage />) },
  { path: '/pack/manage/:id/files', element: authed(<OwnerPackFilesPage />) },
  // B-Pages #3 CJ0w0 回收站 + cSjks 资源包回收站
  { path: '/trash', element: authed(<TrashPage />) },
  { path: '/pack-trash', element: authed(<PackTrashPage />) },
  // B-Pages #4 tC8FY 文件夹详情(直链 URL,深链支持)
  { path: '/folders/:id', element: authed(<FolderDetailPage />) },
  // B-Pages #5 GKK2D 客服工单列表(原只有 /tickets/new + /tickets/:id)
  { path: '/tickets', element: authed(<TicketsPage />) },
  // yOObb 传输列表(文件上传/下载任务,跨页面 zustand store)
  { path: '/transfers', element: authed(<TransfersPage />) },
  { path: '/messages', element: authed(<MessagesPage />) },
  // D6 b63Kr 新建私信独立 route(必须在 /messages/:conversationId 之前;'new' 才不会被当作 conv_id)
  { path: '/messages/new', element: authed(<MessageNewPage />) },
  { path: '/messages/:conversationId', element: authed(<MessageThreadPage />) },
  { path: '/tickets/new', element: authed(<TicketNewPage />) },
  { path: '/tickets/:ticketId', element: authed(<TicketDetailPage />) },
  { path: '/profile/change-password', element: authed(<ChangePasswordPage />) },
  { path: '/profile/settings', element: authed(<SettingsPage />) },
  { path: '/profile/language', element: authed(<LanguagePage />) },
  { path: '/profile/email-bind', element: authed(<EmailBindPage />) },
  { path: '/profile/change-email', element: authed(<ChangeEmailPage />) },
  { path: '/profile/mfa', element: authed(<MfaPage />) },
  { path: '/profile/mfa/backup-codes', element: authed(<BackupCodesPage />) },
  { path: '/profile/edit', element: authed(<ProfileEditPage />) },
  { path: '/profile/notifications', element: authed(<NotificationsPage />) },
  { path: '/profile/payout-accounts', element: authed(<PayoutAccountsPage />) },
  { path: '/profile/oauth-bindings', element: authed(<OAuthBindingsPage />) },
  { path: '/profile/deactivate', element: authed(<DeactivateAccountPage />) },
  { path: '/invite', element: authed(<InvitePage />) },
  { path: '/pack/create', element: authed(<PackCreatePage />) },
  { path: '/orders', element: authed(<OrdersPage />) },

  // 🟢 Public
  // /search 是 🔀 Mixed 页面:未登录显示公开 tab(packs/users/categories),
  // 登录态额外解锁 my_files tab — SearchPage 内部按 useCurrentUser().status 分流。
  { path: '/search', element: <SearchPage /> },
  { path: '/about', element: <AboutPage /> },
  { path: '/auth/login', element: <LoginPage /> },
  // 2026-05-29 fO6Sn 拆独立 route(由 Phase 4-D5 共用 route 反向拆出;
  // 见 plans/2026-05-29-fo6sn-route-split-login-visual-align.md)
  { path: '/auth/login/saved', element: <LoginSavedPage /> },
  { path: '/auth/register', element: <RegisterPage /> },
  { path: '/auth/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/auth/2fa', element: <TwoFactorPage /> },
  // D5 wHWmH 注册邮箱 verify(public;与 D2 tVtVl /profile/email-verify 改邮箱区分)
  { path: '/auth/email-verify', element: <RegisterEmailVerifyPage /> },
  // D3 GmWWr + zPcq8 OAuth 回调(2 frame share component;动态 testid)
  { path: '/oauth/callback', element: <OAuthCallbackPage /> },
  { path: '/a/:userId', element: <AgentPromoRoute /> },
  { path: '/i/:userId', element: <ReferrerPromoRoute /> },
  // B-Pages #1 OhKgp 用户公开主页(免登录直链;agent 推广 / pack 分享归属)
  { path: '/u/:userId', element: <UserProfilePage /> },
  // B-Pages #C 错误页 frame SobPx/ZGZa7/DGwHs/h1EpS/63b6M/HOWuT
  { path: '/403', element: <ForbiddenPage /> },
  { path: '/404', element: <NotFoundPage /> },
  { path: '/500', element: <ServerErrorPage /> },
  { path: '/503', element: <MaintenancePage /> },
  { path: '/offline', element: <OfflinePage /> },
  { path: '/agent-error', element: <AgentErrorPage /> },
  { path: '*', element: <NotFoundPage /> },
]

const router = createHashRouter([
  {
    element: <RootLayout />,
    children: routes.map((r) => {
      const title = r.path ? ROUTE_TITLES[r.path] : undefined
      return title ? { ...r, handle: { title } } : r
    }),
  },
])

// GlobalErrorToast 在 shared 是 framework-agnostic 接受 translate prop;
// user 这里注入 useTranslation().t,让 toast 文案走 user i18n 字典(errors.*)。
function GlobalErrorToastI18n() {
  const { t } = useTranslation()
  return <GlobalErrorToast translate={t} />
}

const el = document.getElementById('root')
if (!el) throw new Error('#root not found')

createRoot(el).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthCacheReset />
      <ToastProvider>
        <GlobalErrorToastI18n />
        <RouterProvider router={router} />
        <InstallPrompt />
        <GlobalCaptchaGate />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
)
